"use client";

import { Box, Container, Grid, Typography } from "@mui/material";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import React from "react";
import { Product } from "src/lib/fetcher/client";
import DynamicCard from "src/lib/components/dynamic-card/default";
import { DynamicCardBlocks, CardTheme } from "src/lib/components/dynamic-card/types";
import { SalifyLinkV2 } from "src/lib/components/ui/link/link";

// =========================================================

export interface VariantProps {
  title: string;
  subTitle?: string;
  backgroundImage?: string;
  seeMoreLink?: string;
  maxProducts?: number;
  theme?: "default" | "haianh" | "bazaar" | "footmart" | "nova-press";
  imagePadding?: number | string;
  products?: Product[];
  sectionId?: string;
}

// Map theme variants to supported CardTheme values
const mapThemeToCardTheme = (theme: VariantProps["theme"]): CardTheme => {
  switch (theme) {
    case "haianh":
      return "haianh";
    case "nova-press":
      return "nova-press";
    case "bazaar":
    case "footmart":
    case "default":
    default:
      return "default";
  }
};

const ProductListSectionFullWidth: React.FC<VariantProps> = ({
  title,
  subTitle,
  backgroundImage,
  seeMoreLink = "/san-pham",
  maxProducts = 6,
  theme = "default",
  imagePadding = 0,
  products = [],
  sectionId,
}) => {
  // Convert products to dynamic card blocks
  const createProductBlocks = (product: Product): DynamicCardBlocks => ({
    image: {
      src: product.imageUrl,
      alt: product.name,
      style: {
        width: "100%",
        height: "240px",
        objectFit: "cover" as const,
      },
      wrapperStyle: {
        padding: imagePadding,
      },
    },
    header: {
      title: {
        text: product.name,
        cssProps: {
          variant: "h6" as const,
          fontWeight: 600,
          color: "#333",
          sx: {
            fontSize: "1.2rem",
            lineHeight: 1.4,
            marginBottom: "12px",
            minHeight: "auto",
          },
        },
      },
    },
    content: {
      description: {
        text: product.description || "",
        cssProps: {
          variant: "body2" as const,
          color: "#666",
          sx: {
            fontSize: "0.875rem",
            lineHeight: 1.5,
            marginBottom: "16px",
            minHeight: "60px",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          },
        },
      },
    },
    actions: {
      buttons: [
        {
          type: "text",
          text: "Xem tất cả sản phẩm →",
          href: `/product/${product.slugName || product.id}`,
          style: {
            fontSize: "0.875rem",
            fontWeight: 500,
            textDecoration: "none",
            color: "#2196F3",
            padding: "0",
            textAlign: "left" as const,
          },
        },
      ],
    },
  });

  // Theme-based styling
  const getBackgroundStyle = () => {
    if (backgroundImage) {
      return {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    }

    // Default gradients for different themes
    const gradients = {
      default: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      haianh: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
      bazaar: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
      footmart: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
      "nova-press": "linear-gradient(135deg, #0f4c75 0%, #3282b8 100%)",
    };

    return {
      background: gradients[theme] || gradients.default,
    };
  };

  const getCardStyle = () => {
    return {
      height: "100%",
      backgroundColor: "#fff",
      borderRadius: "12px",
      border: "none",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
      },
    };
  };

  return (
    <Box
      id={sectionId}
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "70vh",
        ...getBackgroundStyle(),
        display: "flex",
        alignItems: "center",
        py: 8,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: backgroundImage
            ? "linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6))"
            : "linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.4))",
          zIndex: 1,
        },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Title Section */}
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              color: "white",
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.5rem" },
              letterSpacing: "0.02em",
              textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              mb: 2,
            }}
          >
            <SalifyFallback.Label path="title" default={title}>
              {title}
            </SalifyFallback.Label>
          </Typography>
          {subTitle && (
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: 400,
                fontSize: { xs: "1rem", md: "1.2rem" },
                textShadow: "0 1px 4px rgba(0, 0, 0, 0.3)",
              }}
            >
              <SalifyFallback.Label path="subTitle" default={subTitle}>
                {subTitle}
              </SalifyFallback.Label>
            </Typography>
          )}
        </Box>

        {/* Products Grid */}
        <Grid
          container
          spacing={{ xs: 2, md: 3, lg: 4 }}
          sx={{
            mb: 6,
            justifyContent: "center",
          }}
        >
          {products.slice(0, maxProducts).map((product, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <DynamicCard
                variant="vertical"
                imagePosition="top"
                spacing="normal"
                theme={mapThemeToCardTheme(theme)}
                blocks={createProductBlocks(product)}
                link={`/product/${product.slugName || product.id}`}
                showBorder={false}
                showBoxShadow={false}
                containerProps={{
                  sx: {
                    ...getCardStyle(),
                    maxWidth: "360px",
                    width: "100%",
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>

        {/* See More Link */}
        {seeMoreLink && (
          <Box textAlign="center" mt={6}>
            <SalifyLinkV2
              href={seeMoreLink}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                color: "white",
                textDecoration: "none",
                fontSize: "1.1rem",
                fontWeight: 600,
                padding: "12px 24px",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: "8px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.2)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
                },
              }}
            >
              <SalifyFallback.Label path="seeMoreLink" default="Xem tất cả sản phẩm">
                Xem tất cả sản phẩm
              </SalifyFallback.Label>
              <Box component="span" sx={{ fontSize: "1.2rem" }}>
                →
              </Box>
            </SalifyLinkV2>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ProductListSectionFullWidth;
